import React from 'react';
import { useParams } from 'react-router-dom';
import { IconTrophy, IconMapPin, IconBook, IconPlayerPlay, IconShoppingBag, IconUser } from '@tabler/icons-react';
import sports from '../../data/sports';
import ComingSoon from '../../components/common/ComingSoon/ComingSoon';
import SportHero from '../../components/sport/SportHero/SportHero';
import CollapsibleSection from '../../components/sport/CollapsibleSection/CollapsibleSection';
import RulesSection from '../../components/sport/RulesSection/RulesSection';
import CompetitivePlay from '../../components/sport/CompetitivePlay/CompetitivePlay';
import VideosSection from '../../components/sport/VideosSection/VideosSection';
import EquipmentSection from '../../components/sport/EquipmentSection/EquipmentSection';
import CoachSection from '../../components/sport/CoachSection/CoachSection';
import NewsSidebar from '../../components/news/NewsSidebar';
import {
  pickleballRules,
  pickleballGoverningBodies,
  pickleballTournaments,
  pickleballResources
} from '../../data/pickleballData';
import {
  tennisRules,
  tennisGoverningBodies,
  tennisTournaments,
  tennisResources
} from '../../data/tennisData';
import {
  soccerRules,
  soccerGoverningBodies,
  soccerTournaments,
  soccerResources
} from '../../data/soccerData';
import {
  basketballRules,
  basketballGoverningBodies,
  basketballTournaments,
  basketballResources
} from '../../data/basketballData';
import {
  volleyballRules,
  volleyballGoverningBodies,
  volleyballTournaments,
  volleyballResources
} from '../../data/volleyballData';
import {
  padelRules,
  padelGoverningBodies,
  padelTournaments,
  padelResources
} from '../../data/padelData';
import {
  badmintonRules,
  badmintonGoverningBodies,
  badmintonTournaments,
  badmintonResources
} from '../../data/badmintonData';
import {
  squashRules,
  squashGoverningBodies,
  squashTournaments,
  squashResources
} from '../../data/squashData';

const SportDetail = () => {
  const { sportId } = useParams();
  const sport = sports.find((s) => s.id === sportId);

  // Sport data mapping
  const sportDataMap = {
    pickleball: {
      rules: pickleballRules,
      governingBodies: pickleballGoverningBodies,
      tournaments: pickleballTournaments,
      resources: pickleballResources,
      tagline: "America's fastest-growing sport",
      coverImage: 'pickleballphototwo.png'
    },
    tennis: {
      rules: tennisRules,
      governingBodies: tennisGoverningBodies,
      tournaments: tennisTournaments,
      resources: tennisResources,
      tagline: "The sport of elegance and precision",
      coverImage: null
    },
    soccer: {
      rules: soccerRules,
      governingBodies: soccerGoverningBodies,
      tournaments: soccerTournaments,
      resources: soccerResources,
      tagline: "The world's most popular sport",
      coverImage: null
    },
    basketball: {
      rules: basketballRules,
      governingBodies: basketballGoverningBodies,
      tournaments: basketballTournaments,
      resources: basketballResources,
      tagline: "Fast-paced action and high-flying excitement",
      coverImage: null
    },
    volleyball: {
      rules: volleyballRules,
      governingBodies: volleyballGoverningBodies,
      tournaments: volleyballTournaments,
      resources: volleyballResources,
      tagline: "Teamwork, power, and precision",
      coverImage: null
    },
    padel: {
      rules: padelRules,
      governingBodies: padelGoverningBodies,
      tournaments: padelTournaments,
      resources: padelResources,
      tagline: "The fastest-growing racket sport worldwide",
      coverImage: null
    },
    badminton: {
      rules: badmintonRules,
      governingBodies: badmintonGoverningBodies,
      tournaments: badmintonTournaments,
      resources: badmintonResources,
      tagline: "The fastest racket sport in the world",
      coverImage: null
    },
    squash: {
      rules: squashRules,
      governingBodies: squashGoverningBodies,
      tournaments: squashTournaments,
      resources: squashResources,
      tagline: "Intense fitness and strategic gameplay",
      coverImage: null
    }
  };

  if (!sport) {
    return (
      <ComingSoon
        title="Sport Not Found"
        description="The sport you're looking for doesn't exist in our system yet."
      />
    );
  }

  const sportData = sportDataMap[sportId];

  if (!sportData) {
    return (
      <main className="ub-sport-detail-page">
        <div className="ub-sport-detail-container">
          <ComingSoon
            title={`${sport.name} Details Coming Soon`}
            description={`Comprehensive information about ${sport.name} including rules, competitive play, videos, and equipment will be available soon.`}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="ub-sport-detail-page">
      <div className={`ub-sport-detail-wrapper ${sportId === 'pickleball' ? 'ub-with-sidebar' : ''}`}>
        <div className="ub-sport-detail-container">
          <SportHero
            sportName={sport.name}
            coverImage={sportData.coverImage ? `${process.env.PUBLIC_URL}/${sportData.coverImage}` : null}
            tagline={sportData.tagline}
          />

          {sportId === 'pickleball' && (
            <div className="ub-court-finder-container">
              <a
                href="https://www.pickleheads.com/search?lat=40.6940&lng=-73.9901&z=10.0"
                target="_blank"
                rel="noopener noreferrer"
                className="ub-court-finder-button"
              >
                <IconMapPin size={24} />
                <span>Court Finder</span>
              </a>
            </div>
          )}

          <CollapsibleSection
            title="Rules & Gameplay"
            subtitle="Master the fundamentals to dominate the game"
            icon={<IconBook size={28} color="#00d4ff" stroke={2} />}
            isDefaultOpen={true}
          >
            <RulesSection rules={sportData.rules} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Competitive Play"
            subtitle="Tournaments, governing bodies, and resources"
            icon={<IconTrophy size={28} color="#c41e3a" stroke={2} />}
          >
            <CompetitivePlay
              governingBodies={sportData.governingBodies}
              tournaments={sportData.tournaments}
              resources={sportData.resources}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Videos & Training"
            subtitle="Watch tutorials and pro matches"
            icon={<IconPlayerPlay size={28} color="#ffd700" stroke={2} />}
          >
            <VideosSection />
          </CollapsibleSection>

          <CollapsibleSection
            title="Equipment & Gear"
            subtitle="Find the right gear for your game"
            icon={<IconShoppingBag size={28} color="#00d4ff" stroke={2} />}
          >
            <EquipmentSection />
          </CollapsibleSection>

          <CollapsibleSection
            title="Find a Coach"
            subtitle="Connect with experienced coaches near you"
            icon={<IconUser size={28} color="#ffd700" stroke={2} />}
          >
            <CoachSection />
          </CollapsibleSection>
        </div>

        {sportId === 'pickleball' && <NewsSidebar />}
      </div>
    </main>
  );
};

export default SportDetail;
